import {DataTestAttrs, getTestProps} from '@benchling/js-test-utils';
import {Color as NucleusColor} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import produce from 'immer';
import React, {useEffect, useState} from 'react';
import {Handle, Position, useNodeId, useReactFlow} from 'reactflow';

import {
  makeDataFlowEdgeHandle,
  HANDLE_DIRECTION_SOURCE,
  HANDLE_DIRECTION_TARGET,
  HandleDirection,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/flowchartIdentifierUtils';
import {TaskSchemaNodeSectionType} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {TaskSchemaNodeData} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {FlowchartNodeChangeType} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorUtils';
import {FlowchartConfigWithMetadata} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/redux/flowchartConfigAutosaveManagerCallbacks';
import {ReactFlowLockedContext} from 'ui-library/reactflow/LockedContext';
import {BenchlingNode} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNode';
import {ROW_HANDLE_STYLES} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeDesignConstants';
import {ActionButtonConfig} from 'ui-library/reactflow/nodes/BenchlingNode/standard/StandardSectionRow';

import {EdgesContext} from '../EdgesContext';
import {FlowchartEditorModeContext} from '../FlowchartEditorModeContext';
import {FlowchartFeaturesContext} from '../FlowchartFeaturesContext';
import {HandlesContext} from '../HandlesContext';
import {NodesContext} from '../NodesContext';
import {FlowchartEditorMode} from '../ProcessFlowchartEditorModeConstants';
import {findAllDownstreamMappableNodeIds} from '../TraverseFlowchart';
import {
  shouldHideHandle,
  doesRowHaveHandles,
  hasFieldSelectableTab,
  TaskSchemaNodeRowRendererProps,
} from './taskSchemaNodeRowRendererUtils';

/**
 * Handles used to draw edges between fields on two nodes.
 */
function RowHandle(props: {
  rowData: TaskSchemaNodeRowRendererProps;
  handleDirection: HandleDirection;
  isConnected: boolean;
  isHovered: boolean;
  dataTestAttrs?: DataTestAttrs;
}): JSX.Element {
  const [isHandleHovered, setIsHandleHovered] = useState(props.isHovered);
  // Sync handle hover state with parent
  useEffect(() => {
    if (!props.isHovered) {
      setIsHandleHovered(false);
    }
  }, [props.isHovered]);
  const handlesContext = HandlesContext.useContext();
  const editorMode = FlowchartEditorModeContext.useContext();
  const locked = ReactFlowLockedContext.useContext() ?? false;
  const flowchartFeatures = FlowchartFeaturesContext.useContext();
  const currentNodeId = useNodeId();
  const nodesContext = NodesContext.useContext();
  const edgesContext = EdgesContext.useContext();
  const rowHandleId = makeDataFlowEdgeHandle({
    nodeId: currentNodeId ?? '',
    fieldClientId: props.rowData.clientId,
    sectionType: props.rowData.sectionType,
    handleType: props.handleDirection,
  });

  const isDragInProgress = handlesContext.connectionDragState?.isDragging;
  const sourceNodeId = handlesContext.connectionDragState?.sourceHandleInfo?.nodeId;
  const flowchartData = {nodes: nodesContext.nodes, edges: edgesContext.edges};
  const downstreamNodeIdsFromSource =
    isDragInProgress && sourceNodeId != null
      ? findAllDownstreamMappableNodeIds(flowchartData, sourceNodeId)
      : null;
  const downstreamNodeIdsFromCurrentNode =
    currentNodeId != null ? findAllDownstreamMappableNodeIds(flowchartData, currentNodeId) : null;

  // Is the handle dot rendered?
  let isVisible: boolean;
  // Should hover events be triggered? Some handles don't "exist" and should be completely hidden, others appear when hovered
  let isHoverable: boolean;
  if (locked || handlesContext.hideDataFlowEdges) {
    isVisible = false;
    isHoverable = false;
  } else if (props.isConnected) {
    isVisible = true;
    isHoverable = true;
  } else if (editorMode === FlowchartEditorMode.EXECUTION_VIEWER) {
    isVisible = false;
    isHoverable = false;
  } else if (
    shouldHideHandle({
      rowData: props.rowData,
      handleDirection: props.handleDirection,
      connectionDragState: handlesContext.connectionDragState,
      flowchartFeatures,
      currentNodeId,
      downstreamNodeIdsFromSource,
      downstreamNodeIdsFromCurrentNode,
    })
  ) {
    isVisible = false;
    isHoverable = false;
  } else if (isHandleHovered || props.isHovered || isDragInProgress) {
    isVisible = true;
    isHoverable = true;
  } else {
    // The base case renders invisible handles that appear when hovered
    isHoverable = true;
    isVisible = false;
  }

  const isConnectable = (isVisible || isHoverable) && !locked;

  return (
    <Handle
      className={css(
        ROW_HANDLE_STYLES.rowHandle,
        props.handleDirection === HANDLE_DIRECTION_TARGET
          ? ROW_HANDLE_STYLES.leftHandle
          : ROW_HANDLE_STYLES.rightHandle,
        locked && ROW_HANDLE_STYLES.lockedRowHandle,
        !isHoverable && HANDLE_STYLES.noHandle
      )}
      type={props.handleDirection}
      position={props.handleDirection === HANDLE_DIRECTION_TARGET ? Position.Left : Position.Right}
      id={rowHandleId}
      isConnectable={isConnectable}
      {...getTestProps(props.dataTestAttrs)}
      onMouseOver={(e) => {
        setIsHandleHovered(true);
        // Hovering a handle should not trigger the hovered state on the row or node because that will display
        // all handles on the node.
        e.stopPropagation();
      }}
      onMouseOut={() => {
        setIsHandleHovered(false);
      }}
    >
      <div className={css(ROW_HANDLE_STYLES.handleDot, !isVisible && ROW_HANDLE_STYLES.hidden)} />
    </Handle>
  );
}

/**
 * Render a field in the task schema node
 */
export function TaskSchemaNodeRowRenderer(props: TaskSchemaNodeRowRendererProps): JSX.Element {
  const [isRowHovered, setRowIsHovered] = useState(props.isHovered);

  // Note: React Flow v12 (@xyflow/react) has a useNodeData hook that could replace this.
  const currentNodeId = useNodeId();
  const reactFlow = useReactFlow();
  const edgesContext = EdgesContext.useContext();
  const nodeData: TaskSchemaNodeData | null =
    currentNodeId != null ? (reactFlow.getNode(currentNodeId)?.data ?? null) : null;

  const {isSourceConnected, isTargetConnected} = doesRowHaveHandles(
    nodeData?.id ?? null,
    props.clientId,
    props.sectionType,
    edgesContext.edges
  );
  const locked = ReactFlowLockedContext.useContext() ?? false;
  const flowchartFeatures = FlowchartFeaturesContext.useContext();
  const isFieldClickable = hasFieldSelectableTab(nodeData, flowchartFeatures);

  const leftHandle = (
    <RowHandle
      rowData={props}
      isConnected={isTargetConnected}
      handleDirection={HANDLE_DIRECTION_TARGET}
      isHovered={isRowHovered || props.isHovered}
      dataTestAttrs={{
        component: 'TaskSchemaNodeRowRenderer',
        element: 'targetHandle',
        key: props.fieldName,
      }}
    />
  );

  const rightHandle = (
    <RowHandle
      rowData={props}
      isConnected={isSourceConnected}
      handleDirection={HANDLE_DIRECTION_SOURCE}
      isHovered={isRowHovered || props.isHovered}
      dataTestAttrs={{
        component: 'TaskSchemaNodeRowRenderer',
        element: 'sourceHandle',
        key: props.fieldName,
      }}
    />
  );

  const isMapFromStartField = props.sectionType === TaskSchemaNodeSectionType.MAP_FROM_START;
  const canShowDeleteButton = !locked && isMapFromStartField && nodeData != null;
  const isDeleteDisabled = isSourceConnected;

  const handleDeleteField = (): void => {
    if (!nodeData || isDeleteDisabled) {
      return;
    }
    const newNodeData = produce(nodeData, (draftNode) => {
      draftNode.sections = draftNode.sections.map((section) => {
        if (section.sectionType === TaskSchemaNodeSectionType.MAP_FROM_START) {
          section.sectionRows = section.sectionRows.filter((row) => row.id !== props.id);
          return section;
        }
        return section;
      });
    });
    nodeData.onUpdateNodeData(
      (_flowchartConfig: FlowchartConfigWithMetadata) => [
        {id: nodeData.id, data: newNodeData, type: FlowchartNodeChangeType.REPLACE},
      ],
      false,
      true // Skip setting dirty state to prevent autosave when removing map from start field
    );
  };

  const actionButton: ActionButtonConfig | undefined =
    canShowDeleteButton && isRowHovered
      ? {
          icon: 'trash-can',
          onClick: handleDeleteField,
          disabled: isDeleteDisabled,
          tooltip: isDeleteDisabled ? 'Delete all mappings to remove this field' : 'Remove field',
        }
      : undefined;

  return (
    <div
      className={css(
        ROW_STYLES.wrapper,
        isFieldClickable && ROW_STYLES.clickableRow,
        !props.isSelected && isRowHovered && isFieldClickable && ROW_STYLES.hoveredRow,
        props.isSelected && ROW_STYLES.selectedRow
      )}
      onMouseOver={(e) => {
        setRowIsHovered(true);
        e.stopPropagation();
      }}
      onMouseOut={(e) => {
        // Only unhover if moving to an element outside this row.
        // The relatedTarget null check handles JSDOM test environments.
        const relatedTarget = e.relatedTarget as Node | null;
        if (relatedTarget != null && !e.currentTarget.contains(relatedTarget)) {
          setRowIsHovered(false);
        }
        e.stopPropagation();
      }}
      onClick={() => {
        if (nodeData != null && isFieldClickable) {
          nodeData.onFieldClick(nodeData.id, props.clientId, props.id);
        }
      }}
      {...getTestProps({
        component: 'TaskSchemaNodeRowRenderer',
        element: 'root',
        key: props.id,
      })}
    >
      <BenchlingNode.Standard.SectionRow
        primaryText={props.fieldName}
        icon={props.fieldIcon}
        leftHandle={leftHandle}
        rightHandle={rightHandle}
        status={props.status}
        statusDescription={props.statusDescription}
        secondaryIcon={actionButton == null ? props.secondaryIcon : undefined}
        secondaryText={actionButton == null ? props.secondaryText : undefined}
        actionButton={actionButton}
      />
    </div>
  );
}

export const ROW_STYLES = StyleSheet.create({
  wrapper: {
    width: '100%',
    position: 'relative',
  },
  clickableRow: {
    cursor: 'pointer',
  },
  hoveredRow: {
    backgroundColor: NucleusColor.GRAY_50,
  },
  selectedRow: {
    backgroundColor: NucleusColor.BLUE_50,
    color: NucleusColor.BLUE_450,
  },
});

export const HANDLE_STYLES = StyleSheet.create({
  noHandle: {
    pointerEvents: 'none',
    cursor: 'default',
  },
});
